import { useEffect } from 'react';
import { linkedinApi } from '../core/linkedin';

function emitToast(message: string, tone: 'success' | 'error' | 'info' = 'info') {
  window.dispatchEvent(new CustomEvent('plessage:toast', { detail: { message, tone } }));
}

function enhanceSelect(select: HTMLSelectElement) {
  if (select.dataset.plessageEnhanced === '1') return;
  select.dataset.plessageEnhanced = '1';

  const shell = document.createElement('div');
  shell.className = 'plessage-select';
  select.parentNode?.insertBefore(shell, select);
  shell.appendChild(select);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'plessage-select-button';
  button.setAttribute('aria-haspopup', 'listbox');
  shell.appendChild(button);

  const menu = document.createElement('div');
  menu.className = 'plessage-select-menu';
  menu.setAttribute('role', 'listbox');
  shell.appendChild(menu);

  const sync = () => {
    const selectedOption = select.options[select.selectedIndex];
    const nextLabel = selectedOption?.textContent || 'Choose';
    if (button.textContent !== nextLabel) button.textContent = nextLabel;

    const currentSignature = `${select.value}|${Array.from(select.options).map((option) => `${option.value}:${option.textContent}:${option.disabled ? 1 : 0}`).join('|')}`;
    if (menu.dataset.signature === currentSignature) return;
    menu.dataset.signature = currentSignature;
    menu.innerHTML = '';

    Array.from(select.options).forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = option.value === select.value ? 'active' : '';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', option.value === select.value ? 'true' : 'false');
      item.disabled = option.disabled;
      const text = document.createElement('span');
      text.textContent = option.textContent || option.value;
      item.appendChild(text);
      if (option.value === select.value) {
        const check = document.createElement('span');
        check.className = 'plessage-select-check';
        check.textContent = '✓';
        item.appendChild(check);
      }
      item.addEventListener('click', () => {
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        shell.classList.remove('open');
        sync();
      });
      menu.appendChild(item);
    });
  };

  button.addEventListener('click', (event) => {
    event.stopPropagation();
    document.querySelectorAll('.plessage-select.open').forEach((node) => {
      if (node !== shell) node.classList.remove('open');
    });
    shell.classList.toggle('open');
  });
  select.addEventListener('change', sync);
  new MutationObserver(sync).observe(select, { childList: true, subtree: true, attributes: true });
  sync();
}

function currentWorkspaceId() {
  const params = new URL(window.location.href).searchParams;
  return params.get('workspace') || window.localStorage.getItem('plessage.activeWorkspace') || '';
}

function ensureLinkedInAccountsPanel() {
  const tiktokPanel = document.querySelector('.tiktok-accounts-panel');
  if (!tiktokPanel || document.querySelector('.linkedin-accounts-panel')) return;
  const workspaceId = currentWorkspaceId();
  if (!workspaceId) return;

  const section = document.createElement('section');
  section.className = 'accounts-panel linkedin-accounts-panel';
  section.innerHTML = `
    <div class="accounts-panel-head">
      <div>
        <h2>LinkedIn</h2>
        <p>Connect multiple LinkedIn profiles and manage each profile separately for publishing.</p>
      </div>
      <div class="account-actions">
        <a class="primary-button linkedin-connect-button">Connect LinkedIn</a>
      </div>
    </div>
    <div class="account-list linkedin-account-list">
      <div class="content-loading linkedin-loading"><div class="loading-line"></div><span>Loading LinkedIn connections…</span></div>
    </div>`;
  tiktokPanel.insertAdjacentElement('afterend', section);

  const connect = section.querySelector('.linkedin-connect-button') as HTMLAnchorElement | null;
  if (connect) connect.href = linkedinApi.connectUrl(workspaceId);
  const list = section.querySelector('.linkedin-account-list') as HTMLElement | null;
  if (!list) return;

  linkedinApi.list(workspaceId).then((payload) => {
    const accounts = payload.accounts || [];
    if (connect) connect.textContent = accounts.length ? 'Add another LinkedIn account' : 'Connect LinkedIn';
    if (!accounts.length) {
      list.innerHTML = `<div class="empty-state"><div class="linkedin-mark">in</div><h3>No LinkedIn profiles connected yet</h3><p>Connect a LinkedIn profile. You can add more profiles later and publish to each one separately.</p></div>`;
      return;
    }
    list.innerHTML = '';
    accounts.forEach((account) => {
      const row = document.createElement('article');
      row.className = 'account-row';
      const status = account.status && account.status !== 'connected' ? account.status : 'Connected';
      row.innerHTML = `
        <div class="account-avatar linkedin-avatar">${account.picture_url ? `<img src="${account.picture_url.replace(/"/g, '&quot;')}" alt="">` : 'in'}</div>
        <div class="account-details">
          <strong></strong>
          <span>LinkedIn Profile · ${account.member_id}</span>
          ${account.email ? '<span class="linkedin-email"></span>' : ''}
          <div class="capability-list"><small>Planner</small><small>Posts</small></div>
        </div>
        <span class="connected-badge${status === 'Connected' ? '' : ' warning'}">✓ ${status}</span>`;
      const name = row.querySelector('.account-details strong');
      if (name) name.textContent = account.display_name || 'LinkedIn profile';
      const email = row.querySelector('.linkedin-email');
      if (email) email.textContent = account.email || '';
      list.appendChild(row);
    });
  }).catch((reason) => {
    const message = reason instanceof Error ? reason.message : 'LinkedIn connector is not available yet.';
    list.innerHTML = `<div class="status-message"><div class="linkedin-mark">in</div><div><strong>LinkedIn backend is waiting for credentials</strong><span></span></div></div>`;
    const text = list.querySelector('.status-message span');
    if (text) text.textContent = message;
  });
}

export function UiPolish() {
  useEffect(() => {
    const toastRoot = document.createElement('div');
    toastRoot.className = 'plessage-toast-root';
    document.body.appendChild(toastRoot);

    const showToast = (message: string, tone: 'success' | 'error' | 'info' = 'info') => {
      const cleaned = String(message || '').trim();
      if (!cleaned) return;
      const toast = document.createElement('div');
      toast.className = `plessage-toast ${tone}`;
      const icon = document.createElement('span');
      icon.className = 'plessage-toast-icon';
      icon.textContent = tone === 'success' ? '✓' : tone === 'error' ? '!' : 'i';
      const body = document.createElement('div');
      const title = document.createElement('strong');
      title.textContent = tone === 'success' ? 'Done' : tone === 'error' ? 'Something needs attention' : 'Plessage';
      const text = document.createElement('span');
      text.textContent = cleaned;
      body.append(title, text);
      const close = document.createElement('button');
      close.type = 'button';
      close.setAttribute('aria-label', 'Close message');
      close.textContent = '×';
      close.addEventListener('click', () => toast.remove());
      toast.append(icon, body, close);
      toastRoot.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('show'));
      window.setTimeout(() => { toast.classList.remove('show'); window.setTimeout(() => toast.remove(), 220); }, 4200);
    };

    const onToast = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string; tone?: 'success' | 'error' | 'info' }>).detail || {};
      showToast(detail.message || '', detail.tone || 'info');
    };
    window.addEventListener('plessage:toast', onToast as EventListener);

    let pendingComposerAction = '';
    const onDocumentClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      document.querySelectorAll('details.workspace-menu[open]').forEach((details) => {
        if (!details.contains(target)) details.removeAttribute('open');
      });
      document.querySelectorAll('.plessage-select.open').forEach((node) => {
        if (!node.contains(target)) node.classList.remove('open');
      });

      const actionButton = target.closest('.composer-sheet form > footer button') as HTMLButtonElement | null;
      if (actionButton) {
        const label = actionButton.textContent?.trim().toLowerCase() || '';
        if (label.includes('publish')) pendingComposerAction = 'Published successfully.';
        else if (label.includes('schedule')) pendingComposerAction = 'Post scheduled successfully.';
        else if (label.includes('draft')) pendingComposerAction = 'Draft saved successfully.';
      }
    };
    document.addEventListener('click', onDocumentClick, true);

    const knownMessages = new WeakSet<Element>();
    let composerWasOpen = Boolean(document.querySelector('.composer-sheet'));
    const scan = () => {
      document.querySelectorAll('select').forEach((node) => enhanceSelect(node as HTMLSelectElement));
      ensureLinkedInAccountsPanel();

      document.querySelectorAll('.account-customize-fields').forEach((section) => {
        section.querySelectorAll('input, textarea').forEach((field) => {
          const el = field as HTMLInputElement | HTMLTextAreaElement;
          const label = el.closest('label')?.textContent?.toLowerCase() || '';
          let nextPlaceholder = '';
          if (label.includes('title')) nextPlaceholder = 'Uses shared title until you change it';
          else if (label.includes('description')) nextPlaceholder = 'Uses shared description until you change it';
          else if (label.includes('hashtag')) nextPlaceholder = 'Uses shared hashtags until you change them';
          if (nextPlaceholder && el.placeholder !== nextPlaceholder) el.placeholder = nextPlaceholder;
        });
        const help = section.querySelector('.account-thumbnail-field small');
        const nextHelp = 'Uses the shared thumbnail unless you choose another one here.';
        if (help && help.textContent !== nextHelp) help.textContent = nextHelp;
      });

      document.querySelectorAll('.form-error, .status-message.error, .support-success').forEach((node) => {
        if (knownMessages.has(node)) return;
        knownMessages.add(node);
        const message = node.textContent?.trim() || '';
        if (!message) return;
        const success = node.classList.contains('support-success');
        if (!success) pendingComposerAction = '';
        showToast(message, success ? 'success' : 'error');
      });

      const composerOpen = Boolean(document.querySelector('.composer-sheet'));
      if (composerWasOpen && !composerOpen && pendingComposerAction) {
        showToast(pendingComposerAction, 'success');
        pendingComposerAction = '';
      }
      composerWasOpen = composerOpen;
    };

    let scanQueued = false;
    const queueScan = () => {
      if (scanQueued) return;
      scanQueued = true;
      requestAnimationFrame(() => {
        scanQueued = false;
        scan();
      });
    };

    scan();
    const observer = new MutationObserver(queueScan);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      document.removeEventListener('click', onDocumentClick, true);
      window.removeEventListener('plessage:toast', onToast as EventListener);
      toastRoot.remove();
    };
  }, []);

  return null;
}

export { emitToast };
